import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getCartItemRowByLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
        this.#syncEmptyCartUi(true);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Removes a BOGOS gift and re-opens the gift slider so the customer can pick another shade.
   * @param {number} line - The line item index.
   */
  onBogosGiftChange(line) {
    if (this.dataset.bogosChanging === 'true') return;

    const cartItemRow = this.#getCartItemRowByLine(line);
    const changeButton = cartItemRow?.querySelector('.cart-items__change');

    this.#setBogosChanging(true, changeButton);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    })
      .then(async (parsedResponseText) => {
        if (!parsedResponseText || parsedResponseText.errors) return;
        // Morph replaces the slider mount, so re-apply the loader on the fresh node.
        this.#setBogosSliderLoading(true);
        await this.#refreshBogosGiftSlider();
      })
      .finally(() => {
        this.#setBogosChanging(false);
      });
  }

  /**
   * Toggles the loading UI while a BOGOS gift change is in progress.
   * @param {boolean} isChanging
   * @param {Element | null | undefined} [changeButton]
   */
  #setBogosChanging(isChanging, changeButton) {
    this.dataset.bogosChanging = isChanging ? 'true' : 'false';
    this.classList.toggle('bogos-gift-changing', isChanging);
    document.documentElement.classList.toggle('bogos-gift-changing', isChanging);

    const buttons = changeButton instanceof HTMLButtonElement
      ? [changeButton]
      : Array.from(document.querySelectorAll('.cart-items__change'));

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = isChanging;
      button.setAttribute('aria-busy', isChanging ? 'true' : 'false');
      button.classList.toggle('is-loading', isChanging);
    }

    this.#setBogosSliderLoading(isChanging);
  }

  /**
   * Toggles the loading state on BOGOS slider mounts.
   * @param {boolean} isLoading
   */
  #setBogosSliderLoading(isLoading) {
    document.querySelectorAll('.bogos-gift-slider-view').forEach((mount) => {
      mount.classList.toggle('is-loading', isLoading);
      mount.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    });
  }

  /**
   * Finds a cart item row by Shopify cart line number.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRowByLine(line) {
    const byLine = this.querySelector(`[data-line="${line}"]`);
    if (byLine instanceof HTMLTableRowElement) return byLine;

    return this.refs.cartItemRows?.[line - 1];
  }

  /**
   * Re-dispatches the current cart so BOGOS re-renders its gift mounts.
   */
  #notifyBogos = async () => {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) return;

      document.dispatchEvent(new CustomEvent('bogos:gifts', { detail: await response.json() }));
    } catch {
      // Silently ignore BOGOS refresh failures.
    }
  };

  /**
   * Re-triggers BOGOS gift logic and scrolls the embedded slider into view.
   */
  async #refreshBogosGiftSlider() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) return;

      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('bogos:gifts', { detail: cart }));

      // Keep the loader visible briefly so BOGOS has time to mount the slider.
      await new Promise((resolve) => setTimeout(resolve, 600));

      requestAnimationFrame(() => {
        const mount =
          this.querySelector('.bogos-gift-slider-view') || document.querySelector('.bogos-gift-slider-view');
        mount?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } catch {
      // Silently ignore BOGOS refresh failures.
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<object | void>}
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return parsedResponseText;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
        this.#syncEmptyCartUi(newCartItemCount === 0);

        return parsedResponseText;
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      // The re-render resets BOGOS' mount points, so ask BOGOS to redraw the gifts afterwards.
      Promise.resolve(sectionRenderer.renderSection(this.sectionId, { cache: false })).then(this.#notifyBogos);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    const itemCount = event.detail?.data?.itemCount;
    if (typeof itemCount === 'number') {
      this.#syncEmptyCartUi(itemCount === 0);
    }
  };

  /**
   * Toggles empty-cart UI outside the morph target (also-like section, drawer class).
   * @param {boolean} isEmpty
   */
  #syncEmptyCartUi(isEmpty) {
    document.querySelectorAll('[data-cart-also-like]').forEach((el) => {
      el.toggleAttribute('hidden', isEmpty);
    });

    const drawerDialog = document.querySelector('cart-drawer-component dialog');
    drawerDialog?.classList.toggle('cart-drawer--empty', isEmpty);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
